// hooks/usePluginLoader.ts
import { useState, useEffect, useCallback } from "react";
import { BrandingStatus, PaymentData } from "../types";

/**
 * Safety net so the logo area never stays empty forever when the plugin never
 * reports back (blocked iframe, expired paymentId, stalled `/intent`). The
 * plugin has no "intent failed" message, so a timeout is the only signal we get
 * on that path; long enough to cover a Slow 3G `/intent`, short enough that a
 * dead session is not left without a logo.
 */
const BRANDING_TIMEOUT_MS = 8000;

export const usePluginLoader = (config: any) => {
  const [pluginLoaded, setPluginLoaded] = useState(false);
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [paymentId, setPaymentId] = useState("");
  const [brandingStatus, setBrandingStatus] = useState<BrandingStatus>("pending");

  // Branding is resolved once, on the first signal that tells us whether the
  // merchant has its own logo - the `/intent` payload, an error, or the timeout.
  const resolveBranding = useCallback(() => setBrandingStatus("resolved"), []);

  const initializePlugin = useCallback(() => {
    if (!window.plugin) {
      console.error("❌ Plugin is not available.");
      resolveBranding();
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const paymentId = urlParams.get("paymentId") ?? "";
    const accessToken = urlParams.get("accessToken") ?? null;
    const merchantUrl = config?.environment === "local"
      ? "https://hosted.dev.worldline.co.nz/"
      : `${window.location.protocol}//${window.location.host}/`;

    setPaymentId(paymentId);

    window.plugin.init({
      elementId: "divId",
      paymentId,
      accessToken,
      merchantUrl,
      renderTiles: urlParams.get("renderTiles") ?? "true",
      anzBank: urlParams.get("anzBank") ?? "true",
      asbBank: urlParams.get("asbBank") ?? "true",
      bnzBank: urlParams.get("bnzBank") ?? "true",
      westpacBank: urlParams.get("westpacBank") ?? "true",
      coopBank: urlParams.get("coopBank") ?? "true",
      isHosted: "true"
    });

    const paymentData = window.plugin.getPayment();
    if (paymentData) {
      setPaymentData(paymentData);
      resolveBranding();
    }
  }, [config, resolveBranding]);

  useEffect(() => {
    const scriptId = "plugin-js";

    const handlePaymentDataReceived = () => {
      console.log("✅ Payment Data Received");
      setPaymentData(window.plugin?.getPayment() || {});
      resolveBranding();
    };

    // The plugin could not get as far as returning branding, so paint the default.
    const handlePluginError = () => resolveBranding();

    window.addEventListener("paymentDataReceived", handlePaymentDataReceived);
    window.addEventListener("oePluginError", handlePluginError);

    const brandingTimeoutId = window.setTimeout(() => {
      console.warn("⚠️ No payment data within timeout, using the default logo.");
      resolveBranding();
    }, BRANDING_TIMEOUT_MS);

    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = config?.pluginUri;
      script.async = true;

      script.onload = () => {
        console.log("✅ Plugin.js loaded successfully.");
        if (!window.plugin) {
          console.error("❌ Plugin is not available.");
          resolveBranding();
        } else {
          setPluginLoaded(true);
          initializePlugin();
        }
      };

      script.onerror = () => {
        console.error("❌ Failed to load plugin.js.");
        resolveBranding();
      };

      document.body.appendChild(script);
    } else {
      setPluginLoaded(true);
      initializePlugin();
    }

    return () => {
      window.removeEventListener("paymentDataReceived", handlePaymentDataReceived);
      window.removeEventListener("oePluginError", handlePluginError);
      window.clearTimeout(brandingTimeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { pluginLoaded, paymentData, paymentId, setPaymentId, brandingStatus };
};
